import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Search, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import ModuleTile from '../components/ModuleTile';
import { modules } from '../data/modules';
import { useAuth } from '../context/AuthContext';
import { SECTIONS } from '../lib/sectionTheme';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const T = SECTIONS.training;

export default function K4ResourcePage() {
  const { user } = useAuth();
  const [progress, setProgress] = useState([]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/training/progress`, { withCredentials: true }).then(r => setProgress(r.data)).catch(() => {});
  }, [user]);

  const moduleProgress = useMemo(() => {
    const byModule = {};
    progress.forEach(p => {
      if (!byModule[p.module_id]) byModule[p.module_id] = new Set();
      byModule[p.module_id].add(p.step_index);
    });
    return modules.map(m => ({
      ...m,
      completed: byModule[m.id]?.size || 0,
      pct: Math.round(((byModule[m.id]?.size || 0) / m.steps.length) * 100),
    }));
  }, [progress]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="k4-resource-page">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8" data-testid="k4-hero">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1.5" style={{ color: T.color }}>Resource library</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Kestral <span style={{ color: T.color }}>K4</span> Resource
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
            Reference material for the Kestral K4 RIS — reporting, billing, worklists, workflows.
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link to="/search" data-testid="k4-search-link"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: T.color }}>
              <Search className="w-4 h-4" /> Search the library
            </Link>
            <div className="inline-flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 border border-border rounded-lg px-3 py-2" data-testid="k4-chat-hint">
              <Sparkles className="w-3.5 h-3.5" style={{ color: T.color }} />
              <span>Use the K4 Resource Assist chatbot (bottom-right) for instant answers.</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-4 h-4" style={{ color: T.color }} />
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Topics</h2>
          <span className="text-[11px] text-muted-foreground">· {modules.length} modules</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="modules-grid">
          {moduleProgress.map((mod) => <ModuleTile key={mod.id} module={mod} progress={mod.pct} />)}
        </div>
      </div>
    </div>
  );
}
